import { Controller } from "@hotwired/stimulus"

// Inline "vessel busy at this time" warning under the Vessel select. Mirrors
// the "Has conflicts..." badge in Helm's own UI — backed by the same Helm
// endpoint (/api/v1/jobs/resourceschedule), proxied through
// `/orders/vessel_conflicts.json` so the portal owns the authorization +
// privacy filter (no customer names leak across accounts).
//
// Per-Trip-leg scope: each leg has its own vessel select, start input, and
// output slot. Debounced so quickly toggling vessels doesn't flood Helm.
export default class extends Controller {
  static targets = ["vessel", "startAt", "output"]
  static values = { url: String, excludeOrderId: String }

  connect() {
    this.timer = null
    this.check()
  }

  disconnect() { clearTimeout(this.timer) }

  check() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchConflicts(), 250)
  }

  async fetchConflicts() {
    const vessel = this.vesselTarget.value
    const startAt = this.startAtTarget.value
    if (!vessel || !startAt) { this.render([]); return }

    const params = new URLSearchParams({ asset_id: vessel, start_at: startAt })
    if (this.excludeOrderIdValue) params.set("exclude_order_id", this.excludeOrderIdValue)
    try {
      const res = await fetch(`${this.urlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (!res.ok) { this.render([]); return }
      const body = await res.json()
      this.render(body.conflicts || [])
    } catch (_e) {
      this.render([])
    }
  }

  render(conflicts) {
    if (conflicts.length === 0) { this.outputTarget.innerHTML = ""; return }

    const rows = conflicts.map((c) => {
      const start = new Date(c.start)
      const when = isNaN(start) ? c.start : start.toLocaleString("en-AU", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false
      })
      const route = [c.from, c.to].filter(Boolean).join(" → ")
      return `<li>Order #${escapeHtml(c.order_number)} — ${escapeHtml(c.trip_type)} ${escapeHtml(when)}${route ? ` (${escapeHtml(route)})` : ""}</li>`
    }).join("")

    this.outputTarget.innerHTML = `
      <div class="alert alert-warning mt-2 text-xs">
        <div>
          <p class="font-semibold">Vessel is already booked near this time:</p>
          <ul class="list-disc list-inside mt-1 space-y-0.5">${rows}</ul>
        </div>
      </div>
    `
  }
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[c]))
}
